import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiPlus } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { CreateImportDialog } from './CreateImportDialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Skeleton } from '../ui/skeleton'
import { Alert, AlertDescription } from '../ui/alert'
import { useLocale } from '../../contexts/LocaleContext'
import type { ImportRow, ImportStatus } from './importTypes'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { Card } from '../ui/card'

const STATUS_VARIANT: Record<ImportStatus, 'info' | 'success' | 'destructive' | 'secondary'> = {
    running: 'info',
    completed: 'success',
    failed: 'destructive',
    cancelled: 'secondary',
}

/** Status pill for an import, driven by one config map (patterns §15). */
export function ImportStatusBadge({ status }: { status: ImportStatus }) {
    const { t } = useTranslation('imports')

    return (
        <Badge variant={STATUS_VARIANT[status] ?? 'secondary'} data-testid={`import-status-${status}`}>
            {t(`status.${status}`, { defaultValue: status })}
        </Badge>
    )
}

/**
 * Imports list (spec §7): every deposit session, newest first, with its status
 * chip. Clicking a row opens its dashboard.
 */
export function ImportsListPage() {
    const { org } = useParams({ strict: false })
    const { t } = useTranslation('imports')
    const { locale } = useLocale()
    const navigate = useNavigate()
    const [createOpen, setCreateOpen] = useState(false)

    useBreadcrumbs(
        useMemo(
            () => [
                { title: t('common:nav.home'), path: `/${org}` },
                { title: t('breadcrumb') },
            ],
            [org, t],
        ),
    )

    const { data, isLoading, error } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/imports',
        { params: { path: { orgCode: org! } } },
    )

    const imports = ((data as any)?.data ?? []) as ImportRow[]

    const formatDate = (value?: string | null) => {
        if (!value) return '—'
        return new Date(value).toLocaleString(locale)
    }

    return (
        <div className="space-y-6" data-testid="imports-page">
            <PageHeader
                icon={SECTION_ICONS.imports}
                title={t('title')}
                description={t('subtitle')}
                actions={
                    <Button
                        onClick={() => setCreateOpen(true)}
                        aria-label={t('create.button')}
                        title={t('create.button')}
                        data-testid="create-import-btn"
                    >
                        <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('create.button')}</span>
                    </Button>
                }
            />

            <CreateImportDialog
                org={org!}
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={created =>
                    navigate({
                        to: '/$org/imports/$importUid',
                        params: { org: org!, importUid: created.uid },
                    })
                }
            />

            {error && (
                <Alert variant="destructive">
                    <AlertDescription>{t('list.error')}</AlertDescription>
                </Alert>
            )}

            {isLoading && (
                <div className="space-y-2" data-testid="imports-loading">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                </div>
            )}

            {!isLoading && !error && imports.length === 0 && (
                <EmptyState live title={t('list.empty')} data-testid="imports-empty" />
            )}

            {!isLoading && !error && imports.length > 0 && (
                <Card className="overflow-hidden overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('list.label')}</TableHead>
                                <TableHead>{t('list.status')}</TableHead>
                                <TableHead>{t('list.started_at')}</TableHead>
                                <TableHead>{t('list.finished_at')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {imports.map(imp => (
                                <TableRow key={imp.uid} data-testid={`import-row-${imp.uid}`}>
                                    <TableCell>
                                        <Link
                                            to="/$org/imports/$importUid"
                                            params={{ org: org!, importUid: imp.uid }}
                                            className="font-medium text-primary hover:underline"
                                            data-testid={`import-link-${imp.uid}`}
                                        >
                                            {imp.label}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <ImportStatusBadge status={imp.status} />
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDate(imp.started_at)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDate(imp.finished_at)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}
        </div>
    )
}
